import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import i18n, { SUPPORTED_LOCALES, setLocale, type AppLocale } from "@core/i18n";
import { isEnPath } from "../seo/enRoutes";

/**
 * Applique le paramètre d'URL `?lang=fr|en|es` à la langue active.
 *
 * i18next LanguageDetector ne lit le querystring qu'UNE seule fois, au
 * chargement initial de la page. Lors d'une navigation SPA (React Router)
 * vers une URL portant `?lang=xx`, la langue n'était jamais ré-évaluée :
 * le paramètre était ignoré et l'UI restait dans la langue précédente
 * (issue #879). Ce hook resynchronise la langue à chaque changement du
 * paramètre, navigation client comprise.
 *
 * Les pages /en/… (cf. seo/enRoutes.ts) sont anglaises par leur URL : elles
 * passent en anglais quelle que soit la langue mémorisée.
 */
export function useLangQuerySync(): void {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const forceEn = isEnPath(pathname);
  const lang = searchParams.get("lang");

  useEffect(() => {
    if (forceEn) {
      // Langue imposée par l'URL, pas choisie : pas de cookie (sinon visiter
      // une page /en/… redirigerait ensuite l'accueil FR vers /en).
      if (i18n.language?.slice(0, 2) !== "en") void i18n.changeLanguage("en");
      return;
    }
    if (
      lang &&
      SUPPORTED_LOCALES.includes(lang as AppLocale) &&
      i18n.language?.slice(0, 2) !== lang
    ) {
      void setLocale(lang as AppLocale);
    }
  }, [forceEn, lang]);
}

/** Composant utilitaire : monte {@link useLangQuerySync} sans rien rendre. */
export function LangQuerySync(): null {
  useLangQuerySync();
  return null;
}
